/**
 * Role Utilities - Normalized role checking
 * Handles case-insensitive role comparisons to prevent issues with role name variations
 */

/**
 * Normalize role value (case-insensitive)
 * @param {string|undefined|null} role - User role from API/context
 * @returns {string} Normalized lowercase role or empty string
 */
export const normalizeRole = (role) => {
  return (role || "").toLowerCase();
};

/**
 * Check if user is ParkPro Admin
 * @param {string|undefined|null} role - User role
 * @returns {boolean}
 */
export const isParkProAdmin = (role) => {
  return normalizeRole(role) === "parkproadmin";
};

/**
 * Check if user is Agent Admin
 * @param {string|undefined|null} role - User role
 * @returns {boolean}
 */
export const isAgentAdmin = (role) => {
  return normalizeRole(role) === "agentadmin";
};

/**
 * Check if user is agent or agent admin (sees limited destination menu: Disney World + Disneyland Coming Soon)
 * @param {string|undefined|null} role - User role
 * @returns {boolean}
 */
export const isAgentOrAgentAdmin = (role) => {
  const r = normalizeRole(role);
  return r === "agent" || r === "agentadmin";
};

/**
 * Check if user can access admin features
 * @param {string|undefined|null} role - User role
 * @returns {boolean}
 */
export const canAccessAdmin = (role) => {
  return isParkProAdmin(role) || isAgentAdmin(role);
};

/**
 * Check if user can access agency admin features (agency owner/admin)
 * @param {string|undefined|null} role - User role
 * @returns {boolean}
 */
export const canAccessAgencyAdmin = (role) => {
  return isAgentAdmin(role) || isParkProAdmin(role);
};

/**
 * Get role-based access helpers from user object
 * @param {Object|undefined|null} user - User object from context
 * @returns {Object} Object with normalized role and access booleans
 */
export const getRoleAccess = (user) => {
  const role = normalizeRole(user?.role);
  return {
    role,
    isParkProAdmin: isParkProAdmin(role),
    isAgentAdmin: isAgentAdmin(role),
    canAccessAdmin: canAccessAdmin(role),
  };
};

/**
 * Get the landing path for a user role
 * @param {string|undefined|null} role - User role
 * @returns {string} Landing path for the role
 */
export const getLandingPathForRole = (role) => {
  if (canAccessAdmin(role)) {
    return "/agent/admin/dashboard";
  }
  // Default to agent dashboard for normal agents or unknown roles
  return "/agent/dashboard";
};

